import { useEffect, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { fetchData } from '../services';

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const inflightCompanies = new Map();

const loadAdminCompanies = (token, baseUrl) => {
    const key = `${baseUrl}::${token}`;
    const existing = inflightCompanies.get(key);
    if (existing) return existing;

    const request = fetchData(token, `${baseUrl}/apis/admin/companies`)
        .then((response) => (Array.isArray(response?.companies) ? response.companies : []))
        .finally(() => {
            inflightCompanies.delete(key);
        });

    inflightCompanies.set(key, request);
    return request;
};

/**
 * Company record used to fill a project address.
 * Super admins copy the company that owns the project (`project.company_id`).
 * Everyone else copies the company on their own profile.
 */
export const useAddressSourceCompany = (project) => {
    const { token, base_url, user, company } = useAppContext();
    const isSuperAdmin = user?.is_super_admin === true;
    const projectCompanyId = project?.company_id ?? null;
    const ownsProject = sameId(company?.id, projectCompanyId);
    const needsOwningCompany = isSuperAdmin && projectCompanyId != null && !ownsProject;

    const [owningCompany, setOwningCompany] = useState(null);
    const [loadedCompanyId, setLoadedCompanyId] = useState(null);

    useEffect(() => {
        if (!needsOwningCompany || !token) {
            setOwningCompany(null);
            setLoadedCompanyId(null);
            return undefined;
        }

        let cancelled = false;
        setOwningCompany(null);
        setLoadedCompanyId(null);

        loadAdminCompanies(token, base_url)
            .then((companies) => {
                if (cancelled) return;
                setOwningCompany(
                    companies.find((entry) => sameId(entry?.id, projectCompanyId)) || null
                );
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Error loading project company:', error);
                setOwningCompany(null);
            })
            .finally(() => {
                if (!cancelled) setLoadedCompanyId(projectCompanyId);
            });

        return () => {
            cancelled = true;
        };
    }, [needsOwningCompany, token, base_url, projectCompanyId]);

    const owningCompanyReady = sameId(loadedCompanyId, projectCompanyId);
    const addressCompany = needsOwningCompany
        ? (owningCompanyReady && sameId(owningCompany?.id, projectCompanyId) ? owningCompany : null)
        : (company || null);

    return {
        addressCompany,
        isLoadingOwningCompany: needsOwningCompany && !owningCompanyReady,
    };
};
